import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { Btn } from "../components/Btn";
import { Chip } from "../components/Chip";
import { Field } from "../components/Field";
import { Input } from "../components/Input";
import { UserAvatar } from "../components/UserAvatar";
import { useIsDesktop } from "../lib/useIsDesktop";
import { money } from "../lib/format";
import type { MyPledgeResponse } from "../api/types";

const RATE_PRESETS = [0.1, 0.25, 0.5, 0.75, 1.0];

export function Pledge() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isDesktop = useIsDesktop();

  const [goal, setGoal] = useState(1000);
  const [miles, setMiles] = useState(0);
  const [rate, setRate] = useState(0.25);
  const [flat, setFlat] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [closed, setClosed] = useState(false);
  const [gofundme, setGofundme] = useState("");

  useEffect(() => {
    api.get<MyPledgeResponse>("/pledges/me").then((r) => {
      if (typeof r.goal === "number") setGoal(r.goal);
      if (typeof r.miles === "number") setMiles(r.miles);
      setClosed(Boolean(r.closed));
      setGofundme(r.gofundme || "");
      if (r.pledge) {
        setRate(r.pledge.rate);
        setFlat(r.pledge.flat ? String(r.pledge.flat) : "");
      }
    }).catch(() => {});
  }, []);

  const flatNum = Number(flat) || 0;
  const projected = rate * goal + flatNum;
  const owedNow = rate * miles + flatNum;

  const submit = async () => {
    setError(null);
    setSubmitting(true);
    try {
      await api.put("/pledges/me", {
        rate,
        cap: null,
        flat: flatNum,
      });
      setDone(true);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  if (closed) {
    return (
      <div style={{ padding: isDesktop ? "80px 64px" : "32px 22px", textAlign: "center" }}>
        <div style={{ maxWidth: 520, margin: "0 auto" }}>
          <div style={{ fontSize: 40, lineHeight: 1 }}>🏁</div>
          <h1 style={{ margin: "16px 0 0", fontFamily: "var(--display)", fontSize: 36, fontWeight: 400, letterSpacing: -1 }}>
            The fundraiser has closed.
          </h1>
          <p style={{ marginTop: 12, color: "var(--ink-soft)" }}>
            Scott has finished riding, so new pledges are closed. Thank you for your support!
          </p>
          <div style={{ marginTop: 24, display: "flex", gap: 10, justifyContent: "center", flexDirection: isDesktop ? "row" : "column" }}>
            {gofundme && (
              <a href={gofundme} target="_blank" rel="noopener noreferrer" style={{ textDecoration: "none" }}>
                <Btn variant="primary">Donate on GoFundMe →</Btn>
              </a>
            )}
            <Btn variant="secondary" onClick={() => nav("/my-pledge")}>View My Pledge</Btn>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div style={{ padding: isDesktop ? "80px 64px" : "32px 22px", textAlign: "center" }}>
        <div style={{ maxWidth: 520, margin: "0 auto" }}>
          <div
            style={{
              width: 64,
              height: 64,
              borderRadius: 999,
              background: "var(--accent-tint)",
              color: "var(--accent)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 22,
            }}
          >
            ✓
          </div>
          <h1 style={{ margin: 0, fontFamily: "var(--display)", fontSize: 36, fontWeight: 400, letterSpacing: -1 }}>
            Thanks, {user?.first}.
          </h1>
          <p style={{ marginTop: 12, color: "var(--ink-soft)" }}>
            You're on the wall. Scott will pedal harder knowing you're watching.
          </p>
          <div style={{ marginTop: 24, padding: 20, background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 16 }}>
            <Row label="Your rate" value={`$${rate.toFixed(2)} / mile`} />
            {flatNum > 0 && <Row label="Plus flat" value={money(flatNum)} />}
            <Row label={`If Scott hits ${goal.toLocaleString()} mi`} value={money(projected)} accent />
          </div>
          <div style={{ marginTop: 22, display: "flex", gap: 10, justifyContent: "center", flexDirection: isDesktop ? "row" : "column" }}>
            <Btn variant="primary" onClick={() => nav("/")}>Back to home</Btn>
            <Btn variant="secondary" onClick={() => nav("/my-pledge")}>Check My Pledge</Btn>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: isDesktop ? "48px 64px 80px" : "24px 22px 100px" }}>
      <div style={{ maxWidth: 560, margin: "0 auto" }}>
        <h1 style={{ margin: 0, fontFamily: "var(--display)", fontSize: isDesktop ? 44 : 32, fontWeight: 400, letterSpacing: -1 }}>
          Make a Pledge
        </h1>
        <p style={{ marginTop: 10, color: "var(--ink-soft)" }}>
          Pick a rate per mile. Nothing is collected here and there's no payment now — once Scott
          finishes, you'll get an email with a GoFundMe link to donate your pledged amount directly.
        </p>

        <div style={{ marginTop: 28, display: "flex", flexDirection: "column", gap: 18 }}>
          {user && (
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 12 }}>
              <UserAvatar first={user.first} last={user.last} size={36} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 11, color: "var(--ink-soft)", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 600 }}>
                  Signed in as
                </div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>
                  {user.first} {user.last}
                </div>
                <div style={{ fontSize: 12, color: "var(--ink-soft)" }}>{user.email}</div>
              </div>
            </div>
          )}

          <Field label="Pledge rate per mile">
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
              {RATE_PRESETS.map((r) => (
                <Chip key={r} active={rate === r} onClick={() => setRate(r)}>
                  ${r.toFixed(2)}/mi
                </Chip>
              ))}
              <Chip active={!RATE_PRESETS.includes(rate)} onClick={() => setRate(0.5)}>
                Custom
              </Chip>
            </div>
            <Input
              type="number"
              step="0.05"
              value={String(rate)}
              onChange={(v) => setRate(Number(v) || 0)}
              prefix="$"
            />
          </Field>

          <details style={{ background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 12, padding: "14px 16px" }}>
            <summary style={{ cursor: "pointer", fontWeight: 600, fontSize: 14 }}>+ Optional: add a flat amount</summary>
            <div style={{ marginTop: 14, display: "flex", flexDirection: "column", gap: 14 }}>
              <Field label="Flat amount" hint="Add a fixed donation on top of per-mile.">
                <Input type="number" value={flat} onChange={setFlat} placeholder="100" prefix="$" />
              </Field>
            </div>
          </details>
        </div>

        <div style={{ marginTop: 24, padding: 18, background: "var(--accent-tint)", borderRadius: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: "var(--accent)", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>
            Live preview
          </div>
          <div style={{ fontFamily: "var(--display)", fontSize: 22, fontWeight: 400, letterSpacing: -0.4 }}>
            If Scott hits {goal.toLocaleString()} miles, your donation total will be{" "}
            <strong style={{ color: "var(--accent)" }}>{money(projected)}</strong>.
          </div>
          <div style={{ fontSize: 12, color: "var(--ink-soft)", marginTop: 6 }}>
            Right now (at {miles} mi) it would be {money(owedNow)}.
          </div>
        </div>

        {error && (
          <div style={{ marginTop: 16, padding: 12, background: "oklch(0.96 0.04 30)", color: "oklch(0.50 0.16 30)", borderRadius: 8, fontSize: 13 }}>
            {error}
          </div>
        )}

        <Btn variant="primary" size="lg" full onClick={submit} disabled={submitting || rate <= 0} style={{ marginTop: 22 }}>
          {submitting ? "Saving…" : "Lock in my pledge"}
        </Btn>
      </div>
    </div>
  );
}

function Row({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 0" }}>
      <span style={{ color: "var(--ink-soft)" }}>{label}</span>
      <span style={{ fontWeight: 700, color: accent ? "var(--accent)" : "var(--ink)" }}>{value}</span>
    </div>
  );
}
